/**
 * Pure helpers for the Gladly Chat widget and Help Center (OWN-2456), kept
 * JSX-free and import-free so they can be unit-tested under the repo's
 * Jest/Babel setup (preset-env only). The React code that uses them lives in
 * the app shell and the Help Center page.
 *
 * The constants encode Gladly contracts documented in
 * docs/research/gladly-chat-and-help-center.md: the prod api/cdn hosts (one
 * Gladly org serves every environment), the Help Center loader script
 * (hcl.js), and the container selector the loader mounts into.
 */

export const HELP_CENTER_SELECTOR = '#gladly-help-center';
export const HELP_CENTER_LOADER_URL =
  'https://cdn.gladly.com/help-center/hcl.js';
export const HELP_CENTER_LOADER_ID = 'gladly-help-center-loader';

export const GLADLY_API_HOST = 'https://havenly.us-1.gladly.com';
export const GLADLY_CDN_HOST = 'https://cdn.gladly.com';

const HELP_CENTER_PATH = '/faq';

/**
 * Build the window.gladlyHCConfig object for the Help Center loader.
 * @param {{orgId: string, brandId: string}} settings
 * @returns {{api: string, orgId: string, brandId: string, cdn: string, selector: string}}
 */
export function buildHelpCenterConfig(settings) {
  return {
    api: GLADLY_API_HOST,
    orgId: settings.orgId.trim(),
    brandId: settings.brandId.trim(),
    cdn: GLADLY_CDN_HOST,
    selector: HELP_CENTER_SELECTOR
  };
}

/**
 * Lowercase a pathname and strip trailing slashes ('/' stays '/').
 * @param {string|null|undefined} pathname
 * @returns {string} '' for non-strings
 */
export function normalizePathname(pathname) {
  if (typeof pathname !== 'string') return '';
  const lowered = pathname.toLowerCase();
  if (/^\/+$/.test(lowered)) return '/';
  return lowered.replace(/\/+$/, '');
}

/**
 * True when the pathname is the Help Center page.
 * @param {string} pathname
 * @returns {boolean}
 */
export function isHelpCenterPage(pathname) {
  return normalizePathname(pathname) === HELP_CENTER_PATH;
}

/**
 * Whether a client-side navigation must become a full page load: the Help
 * Center loader only works on a fresh document, so entering or leaving /faq
 * reloads. A missing previous location is a first load, never a navigation
 * (reload-loop guard).
 * @param {string|null|undefined} fromPathname
 * @param {string} toPathname
 * @returns {boolean}
 */
export function shouldForceFullPageLoad(fromPathname, toPathname) {
  if (typeof fromPathname !== 'string' || fromPathname === '') return false;
  const from = normalizePathname(fromPathname);
  const to = normalizePathname(toPathname);
  if (from === to) return false;
  return (from === HELP_CENTER_PATH) !== (to === HELP_CENTER_PATH);
}

/**
 * Tell the Gladly widget the SPA route changed. Never throws.
 * @param {object} win
 * @returns {void}
 */
export function notifyGladlyNavigate(win) {
  try {
    if (win && win.Gladly && typeof win.Gladly.navigate === 'function') {
      win.Gladly.navigate();
    }
  } catch (e) {
    /* third-party SDK failure must never break navigation — ignore */
  }
}

/**
 * Inject the Help Center loader once. Returns false (and changes nothing) if
 * the loader script is already present.
 * @param {Document} doc
 * @param {object} win
 * @param {object} config
 * @returns {boolean}
 */
export function loadHelpCenter(doc, win, config) {
  if (doc.getElementById(HELP_CENTER_LOADER_ID)) return false;
  win.gladlyHCConfig = config;
  const script = doc.createElement('script');
  script.id = HELP_CENTER_LOADER_ID;
  script.async = true;
  script.src = HELP_CENTER_LOADER_URL;
  doc.head.appendChild(script);
  return true;
}
